/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CrmDataEnrichmentFields.module.scss';

type Translate = (key: string) => string;

type Cadence = 'daily' | 'filing' | 'event';

const getCadenceLabel = (t: Translate): Record<Cadence, string> => ({
  daily: t('Daily-synced'),
  filing: t('On filing'),
  event: t('Event-driven'),
});

const getFieldGroups = (t: Translate) => [
  {
    title: t('Identity'),
    cadence: 'daily' as Cadence,
    fields: t(
      'Registered legal name, trading name, registration number, jurisdiction, entity type, status, incorporation date',
    ),
    source: t('National company register'),
  },
  {
    title: t('Location'),
    cadence: 'daily' as Cadence,
    fields: t('Registered address, trading addresses, country, region, postcode'),
    source: t('National company register'),
  },
  {
    title: t('Identifiers'),
    cadence: 'daily' as Cadence,
    fields: t('VAT number, LEI, tax identifier, web domain'),
    source: t('Tax authority and GLEIF'),
  },
  {
    title: t('Classification'),
    cadence: 'filing' as Cadence,
    fields: t('SIC, NACE, NAICS, industry description, activity text as filed'),
    source: t('National company register'),
  },
  {
    title: t('Size'),
    cadence: 'filing' as Cadence,
    fields: t('Employee count, employee band — modelled where not filed'),
    source: t('Filed accounts and model'),
  },
  {
    title: t('Financials'),
    cadence: 'filing' as Cadence,
    fields: t('Turnover, profit, net assets, total assets, filing date, currency'),
    source: t('Filed statutory accounts'),
  },
  {
    title: t('Ownership'),
    cadence: 'event' as Cadence,
    fields: t('Shareholders, beneficial owners, immediate parent, ultimate parent, group entities'),
    source: t('Register of beneficial ownership'),
  },
  {
    title: t('People'),
    cadence: 'event' as Cadence,
    fields: t('Directors and officers, role, appointment and resignation dates'),
    source: t('National company register'),
  },
];

export const CrmDataEnrichmentFields = () => {
  const { t } = useInt();

  const CADENCE_LABEL = getCadenceLabel(t);
  const FIELD_GROUPS = getFieldGroups(t);

  const headRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pullRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const revealables = [headRef.current, ...cardRefs.current, pullRef.current].filter((el): el is HTMLDivElement =>
      Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock align="left" title={t('Every field arrives with its source attached.')} />
      </div>

      <div className={styles.grid}>
        {FIELD_GROUPS.map((group, i) => (
          <div
            key={group.title}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            className={cn(styles.card, styles.reveal)}
            style={{ transitionDelay: `${(i % 4) * 70}ms` }}
          >
            <div className={styles.cardHead}>
              <h3 className={styles.cardTitle}>{group.title}</h3>
              <span className={cn(styles.cadence, styles[`cadence_${group.cadence}`])}>
                {CADENCE_LABEL[group.cadence]}
              </span>
            </div>

            <p className={styles.cardFields}>{group.fields}</p>
            <p className={styles.cardSource}>{group.source}</p>
          </div>
        ))}
      </div>

      <div ref={pullRef} className={cn(styles.pull, styles.reveal)}>
        <span className={styles.pullMark}>“</span>
        <p className={styles.pullText}>
          {t(
            'A modelled value says so on the field, and a value we do not hold stays empty. We will not fill a gap with an estimate and let it pass as filed.',
          )}
        </p>
      </div>
    </Section>
  );
};
